import React, { useState, useEffect, useMemo, useCallback } from 'react';
import api from '../../lib/api';
import { INCIDENT_CATEGORIES, INCIDENT_STATUS_COLORS, SHIPMENT_STATUS_LABELS } from '../../lib/constants';
import { CountryRoute } from '../../components/CountryDisplay';
import ProfessionalLayout from '../../components/ProfessionalLayout';
import '../../styles/professional-theme.css';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Truck, Package, CheckCircle, Clock, Camera, FileText, MapPin,
  Scan, User, Phone, AlertCircle, Package2, PackageCheck, ChevronDown, ChevronUp,
  AlertTriangle, Upload, RefreshCw, History, DollarSign, Wallet, CreditCard, Banknote,
  Filter, X, Search, KeyRound, Send, Lock
} from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import SEOHead from '../../components/SEOHead';
import GeographicFilter from '../../components/GeographicFilter';
import { useGeographicData } from '../../hooks/useGeographicData';
import SignaturePad from '../../components/SignaturePad';
import ShipmentScanDialog from '../../components/ShipmentScanDialog';

const extractErrorMessage = (err, fallback = 'Erreur inconnue') => {
  const detail = err?.response?.data?.detail;
  if (!detail) return err?.userMessage || err?.message || fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(d => d.msg || JSON.stringify(d)).join(', ');
  return JSON.stringify(detail);
};

const LocalPartnerDashboard = () => {
  const { t, language } = useLanguage();
  const { getCitiesForCountry } = useGeographicData();
  const [shipments, setShipments] = useState([]);
  const [selectedShipment, setSelectedShipment] = useState(null);
  const [shipmentPackages, setShipmentPackages] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Unified delivery flow state
  const [showDeliveryFlow, setShowDeliveryFlow] = useState(false);
  const [deliveryFlowPkg, setDeliveryFlowPkg] = useState(null);
  const [deliveryStep, setDeliveryStep] = useState('choose'); // 'choose' | 'hand' | 'relay'
  const [deliverySubmitting, setDeliverySubmitting] = useState(false);
  // Hand delivery fields
  const [deliveryNotes, setDeliveryNotes] = useState('');
  const [deliverySignature, setDeliverySignature] = useState('');
  const [deliveryCode, setDeliveryCode] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  const [startingDelivery, setStartingDelivery] = useState(null);
  // Relay delivery fields
  const [relayCity, setRelayCity] = useState('');
  const [relayPhoto, setRelayPhoto] = useState(null);
  const [relayNotes, setRelayNotes] = useState('');
  // Scanner state
  const [scannerOceCode, setScannerOceCode] = useState('');
  const [scannerNotes, setScannerNotes] = useState('');
  const [scannerCode, setScannerCode] = useState(''); // code de livraison (OTP) fourni par le destinataire
  const [otpEnabled, setOtpEnabled] = useState(true); // OTP de livraison exigé par l'entreprise (défaut : oui)
  // Déchargement (scan réception d'un départ à l'arrivée)
  const [unloadScan, setUnloadScan] = useState({ open: false, shipmentId: null });

  // Incidents state
  const [incidents, setIncidents] = useState([]);
  const [incidentsLoading, setIncidentsLoading] = useState(false);
  const [showIncidentDialog, setShowIncidentDialog] = useState(false);
  const [incidentPackageId, setIncidentPackageId] = useState('');
  const [incidentCategory, setIncidentCategory] = useState('');
  const [incidentDescription, setIncidentDescription] = useState('');
  const [incidentPhoto, setIncidentPhoto] = useState(null);
  const [incidentSubmitting, setIncidentSubmitting] = useState(false);


  // Delivery history state
  const [deliveryHistory, setDeliveryHistory] = useState([]);
  const [completedShipments, setCompletedShipments] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historySearch, setHistorySearch] = useState('');
  const [expandedHistoryShipment, setExpandedHistoryShipment] = useState(null);

  // Finance state
  const [financeData, setFinanceData] = useState(null);
  const [financeLoading, setFinanceLoading] = useState(false);

  // Expense reports state
  const [expenses, setExpenses] = useState([]);
  const [expensesLoading, setExpensesLoading] = useState(false);
  const [showExpenseDialog, setShowExpenseDialog] = useState(false);
  const [expenseForm, setExpenseForm] = useState({ category: 'transport', amount: '', description: '', expense_date: new Date().toISOString().slice(0, 10), receipt_photo: '' });
  const [expenseSubmitting, setExpenseSubmitting] = useState(false);
  const [expenseFeatureEnabled, setExpenseFeatureEnabled] = useState(false);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [paymentPackage, setPaymentPackage] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [paymentNote, setPaymentNote] = useState('');
  const [paymentSubmitting, setPaymentSubmitting] = useState(false);

  // Filter state
  const [filterCountry, setFilterCountry] = useState('');
  const [filterCity, setFilterCity] = useState('');
  const [filterPaymentStatus, setFilterPaymentStatus] = useState('');
  const [filterPaymentMethod, setFilterPaymentMethod] = useState('');
  const [filterPaymentLocation, setFilterPaymentLocation] = useState('');

  const [packageSearch, setPackageSearch] = useState('');

  const hasActiveFilters = filterCountry || filterCity || filterPaymentStatus || filterPaymentMethod || filterPaymentLocation;

  const resetFilters = () => {
    setFilterCountry('');
    setFilterCity('');
    setFilterPaymentStatus('');
    setFilterPaymentMethod('');
    setFilterPaymentLocation('');
  };

  useEffect(() => {
    fetchShipments();
    fetchIncidents();
    checkExpenseFeature();
    // Savoir si l'entreprise exige l'OTP de livraison (affichage du champ code).
    api.get('/api/company/transport-modes')
      .then(r => setOtpEnabled(r.data?.delivery_otp_required !== false))
      .catch(() => {});
  }, []);

  // Re-fetch when geo filters change
  useEffect(() => {
    fetchShipments();
    if (financeData) fetchFinance();
  }, [filterCountry, filterCity]);

  const fetchShipments = async () => {
    try {
      const params = {};
      if (filterCountry) params.destination_country = filterCountry;
      if (filterCity) params.destination_city = filterCity;
      const response = await api.get('/api/local-partner/shipments', { params });
      setShipments(response.data);
    } catch (err) {
      console.error('Error fetching shipments:', err);
      setError(t('errorLoading'));
    } finally {
      setLoading(false);
    }
  };

  const fetchShipmentPackages = async (shipmentId) => {
    try {
      const response = await api.get(`/api/local-partner/shipment/${shipmentId}/packages`, {
        params: { _t: Date.now() },
      });
      setShipmentPackages(response.data);
      setSelectedShipment(shipmentId);
    } catch (err) {
      console.error('Error fetching shipment packages:', err);
      setError(t('errorLoading'));
    }
  };

  const fetchIncidents = async () => {
    setIncidentsLoading(true);
    try {
      const response = await api.get('/api/incidents', {
        params: { limit: 100 }
      });
      setIncidents(response.data.data || []);
    } catch (err) {
      console.error('Error fetching incidents:', err);
    } finally {
      setIncidentsLoading(false);
    }
  };

  const fetchDeliveryHistory = async () => {
    setHistoryLoading(true);
    try {
      const response = await api.get('/api/local-partner/delivery-history');
      setDeliveryHistory(response.data.deliveries || []);
      setCompletedShipments(response.data.completed_shipments || []);
    } catch (err) {
      console.error('Error fetching delivery history:', err);
    } finally {
      setHistoryLoading(false);
    }
  };

  const fetchFinance = async () => {
    setFinanceLoading(true);
    try {
      const params = {};
      if (filterCountry) params.destination_country = filterCountry;
      if (filterCity) params.destination_city = filterCity;
      if (filterPaymentStatus) params.payment_status = filterPaymentStatus;
      if (filterPaymentMethod) params.payment_method = filterPaymentMethod;
      if (filterPaymentLocation) params.payment_location = filterPaymentLocation;
      const response = await api.get('/api/local-partner/finance-summary', { params });
      setFinanceData(response.data);
    } catch (err) {
      console.error('Error fetching finance:', err);
    } finally {
      setFinanceLoading(false);
    }
  };

  // Expense reports
  const checkExpenseFeature = async () => {
    try {
      const res = await api.get('/api/company/settings');
      setExpenseFeatureEnabled(res.data?.enabled_features?.expense_reports || false);
    } catch { /* feature stays disabled */ }
  };

  const fetchExpenses = async () => {
    setExpensesLoading(true);
    try {
      const res = await api.get('/api/expenses');
      setExpenses(res.data);
    } catch (err) {
      console.error('Error fetching expenses:', err);
    } finally {
      setExpensesLoading(false);
    }
  };

  const handleCreateExpense = async (e) => {
    e.preventDefault();
    if (expenseSubmitting) return;
    setExpenseSubmitting(true);
    setError('');
    try {
      await api.post('/api/expenses', {
        category: expenseForm.category,
        amount: parseFloat(expenseForm.amount),
        description: expenseForm.description,
        expense_date: expenseForm.expense_date,
        receipt_photo: expenseForm.receipt_photo || null,
      });
      setSuccess(language === 'en' ? 'Expense report created' : 'Note de frais créée');
      setShowExpenseDialog(false);
      setExpenseForm({ category: 'transport', amount: '', description: '', expense_date: new Date().toISOString().slice(0, 10), receipt_photo: '' });
      fetchExpenses();
    } catch (err) {
      setError(err.userMessage || 'Erreur lors de la création');
    } finally {
      setExpenseSubmitting(false);
    }
  };

  const handleDeleteExpense = async (id) => {
    try {
      await api.delete(`/api/expenses/${id}`);
      setExpenses(prev => prev.filter(e => e.id !== id));
      setSuccess(language === 'en' ? 'Expense deleted' : 'Note supprimée');
    } catch (err) {
      setError(err.userMessage || 'Erreur');
    }
  };

  const EXPENSE_CATEGORIES = {
    fr: { transport: 'Transport', fuel: 'Carburant', handling: 'Manutention', phone: 'Téléphone', parking: 'Parking', other: 'Autre' },
    en: { transport: 'Transport', fuel: 'Fuel', handling: 'Handling', phone: 'Phone', parking: 'Parking', other: 'Other' },
  };
  const catLabels = EXPENSE_CATEGORIES[language] || EXPENSE_CATEGORIES.fr;

  const openPaymentDialog = (pkg) => {
    setPaymentPackage(pkg);
    const price = pkg.price || pkg.total_price || 0;
    const paid = pkg.payment_status === 'paid' ? price : (pkg.advance_amount || 0);
    const remaining = Math.max(0, price - paid);
    setPaymentAmount(remaining > 0 ? String(remaining) : '');
    setPaymentMethod('cash');
    setPaymentNote('');
    setShowPaymentDialog(true);
  };

  const handleRecordPayment = async (e) => {
    e.preventDefault();
    if (!paymentPackage || !paymentAmount) return;
    setPaymentSubmitting(true);
    setError('');
    try {
      const res = await api.post('/api/local-partner/collect-payment', {
        oce_code: paymentPackage.oce_code,
        amount: parseFloat(paymentAmount),
        payment_method: paymentMethod,
        note: paymentNote,
      });
      const newStatus = res.data?.new_payment_status;
      const remaining = res.data?.remaining ?? 0;

      // Optimistic update: patch local shipmentPackages state immediately
      if (shipmentPackages) {
        const patchPkg = (pkg) => {
          if (pkg.oce_code !== paymentPackage.oce_code) return pkg;
          return {
            ...pkg,
            payment_status: newStatus,
            advance_amount: newStatus === 'paid' ? 0 : (res.data?.total_paid ?? pkg.advance_amount),
          };
        };
        setShipmentPackages((prev) => prev ? {
          ...prev,
          ready_for_delivery: (prev.ready_for_delivery || []).map(patchPkg),
          locked_for_delivery: (prev.locked_for_delivery || []).map(patchPkg),
          delivered_packages: (prev.delivered_packages || []).map(patchPkg),
          awaiting_customs: (prev.awaiting_customs || []).map(patchPkg),
          at_relay_point: (prev.at_relay_point || []).map(patchPkg),
          other_packages: (prev.other_packages || []).map(patchPkg),
        } : prev);
      }

      setShowPaymentDialog(false);
      setPaymentPackage(null);
      const statusLabel = newStatus === 'paid' ? 'Payé' :
                          newStatus === 'advance' ? `Acompte — reste ${fmt(remaining)}` : 'Enregistré';
      setSuccess(`Paiement de ${paymentAmount}€ enregistré — ${statusLabel}`);

      // Background refresh from server for full consistency
      fetchShipments();
      if (selectedShipment) fetchShipmentPackages(selectedShipment);
      if (financeData) fetchFinance();
    } catch (err) {
      setError(extractErrorMessage(err, 'Erreur lors de l\'enregistrement du paiement'));
    } finally {
      setPaymentSubmitting(false);
    }
  };

  const openDeliveryFlow = (pkg) => {
    setDeliveryFlowPkg(pkg);
    setDeliveryStep('choose');
    setDeliveryNotes('');
    setDeliverySignature('');
    setDeliveryCode('');
    setResendCooldown(0);
    setRelayCity('');
    setRelayPhoto(null);
    setRelayNotes('');
    setShowDeliveryFlow(true);
  };

  const closeDeliveryFlow = () => {
    setShowDeliveryFlow(false);
    setDeliveryFlowPkg(null);
    setDeliveryStep('choose');
    setDeliveryCode('');
    setResendCooldown(0);
  };

  const handleStartDelivery = async (pkg) => {
    setStartingDelivery(pkg.id);
    setError('');
    try {
      // Note obligatoire côté API pour tout changement de statut.
      await api.put(`/api/packages/${pkg.id}/status`, { status: 'out_for_delivery', note: 'Mise en livraison — code envoyé au client' });
      setSuccess(`Code de livraison envoyé au destinataire pour le colis ${pkg.oce_code}`);
      // Refléter le nouveau statut dans le flow ouvert (bascule le bouton vers « Renvoyer »).
      setDeliveryFlowPkg(prev => (prev && prev.id === pkg.id ? { ...prev, status: 'out_for_delivery' } : prev));
      fetchShipments();
      if (selectedShipment) fetchShipmentPackages(selectedShipment);
    } catch (err) {
      setError(extractErrorMessage(err, 'Erreur lors du changement de statut'));
    } finally {
      setStartingDelivery(null);
    }
  };

  // Déchargement individuel d'un colis (scan réception ciblé) : le débloque
  // pour la livraison sans passer par le scan global du départ.
  const [unloadingPkg, setUnloadingPkg] = useState(null);
  const handleUnloadPackage = async (pkg) => {
    if (!selectedShipment) return;
    setUnloadingPkg(pkg.id);
    setError('');
    try {
      await api.post(`/api/shipments/${selectedShipment}/scan`, {
        oce_code: pkg.oce_code,
        checkpoint: 'reception',
      });
      setSuccess(`Colis ${pkg.oce_code} déchargé — disponible pour livraison.`);
      fetchShipmentPackages(selectedShipment);
    } catch (err) {
      setError(extractErrorMessage(err, 'Erreur lors du déchargement'));
    } finally {
      setUnloadingPkg(null);
    }
  };

  const handleResendCode = async () => {
    if (!deliveryFlowPkg || resendCooldown > 0) return;
    try {
      await api.post(`/api/packages/${deliveryFlowPkg.id}/resend-delivery-code`);
      setSuccess('Code renvoyé au destinataire');
      setResendCooldown(60);
      const interval = setInterval(() => {
        setResendCooldown((prev) => {
          if (prev <= 1) { clearInterval(interval); return 0; }
          return prev - 1;
        });
      }, 1000);
    } catch (err) {
      setError(extractErrorMessage(err, 'Erreur lors du renvoi du code'));
    }
  };

  // Clé destinataire : téléphone (prioritaire) sinon email sinon nom.
  const recipientKey = (p) => (p.recipient_phone || p.recipient_email || p.recipient_name || '').trim().toLowerCase();

  // Colis encore à livrer pour le même destinataire (hors colis courant) dans ce départ.
  const remainingForRecipient = (pkg) => {
    if (!shipmentPackages) return [];
    const key = recipientKey(pkg);
    if (!key) return [];
    const pool = [
      ...(shipmentPackages.ready_for_delivery || []),
      ...(shipmentPackages.locked_for_delivery || []),
      ...(shipmentPackages.at_relay_point || []),
    ];
    return pool.filter(p => p.oce_code !== pkg.oce_code && recipientKey(p) === key);
  };

  // Message de livraison + rappel des colis restants pour ce client (évite les allers-retours).
  const deliverySuccessMsg = (pkg) => {
    const base = `Livraison du colis ${pkg.oce_code} validée avec succès !`;
    const rest = remainingForRecipient(pkg);
    if (rest.length === 0) return `${base} Tous les colis de ${pkg.recipient_name || 'ce client'} dans ce départ sont livrés.`;
    return `${base} Il reste ${rest.length} colis à livrer pour ${pkg.recipient_name || 'ce client'} : ${rest.map(p => p.oce_code).join(', ')}`;
  };

  const handleHandDelivery = async () => {
    if (!deliveryFlowPkg) return;
    setDeliverySubmitting(true);
    setError('');
    const deliveredPkg = deliveryFlowPkg;
    try {
      await api.post('/api/local-partner/validate-delivery', {
        oce_code: deliveryFlowPkg.oce_code,
        delivery_notes: deliveryNotes || '',
        recipient_signature: deliverySignature || '',
        delivery_photo: '',
        verification_code: deliveryCode || undefined,
      });
      setSuccess(deliverySuccessMsg(deliveredPkg));
      closeDeliveryFlow();
      fetchShipments();
      if (selectedShipment) fetchShipmentPackages(selectedShipment);
    } catch (err) {
      setError(extractErrorMessage(err, t('errorStatusChange')));
    } finally {
      setDeliverySubmitting(false);
    }
  };

  const handleRelayDelivery = async () => {
    if (!deliveryFlowPkg || !relayCity || !relayPhoto?.base64) return;
    setDeliverySubmitting(true);
    setError('');
    try {
      await api.post('/api/local-partner/assign-delivery-mode', {
        oce_code: deliveryFlowPkg.oce_code,
        delivery_mode: 'relay_point',
        relay_point_id: relayCity,
      });
      await api.post('/api/local-partner/deposit-relay-point', {
        oce_code: deliveryFlowPkg.oce_code,
        relay_point_id: relayCity,
        delivery_notes: relayNotes || null,
        delivery_photo: relayPhoto.base64,
      });
      setSuccess(`Colis ${deliveryFlowPkg.oce_code} déposé en point relais avec succès !`);
      closeDeliveryFlow();
      fetchShipments();
      if (selectedShipment) fetchShipmentPackages(selectedShipment);
    } catch (err) {
      setError(extractErrorMessage(err, t('errorDeposit')));
    } finally {
      setDeliverySubmitting(false);
    }
  };

  const openIncidentDialog = (packageId = '') => {
    setIncidentPackageId(packageId);
    setIncidentCategory('');
    setIncidentDescription('');
    setIncidentPhoto(null);
    setShowIncidentDialog(true);
  };

  const handleSubmitIncident = async (e) => {
    e.preventDefault();
    if (!incidentPackageId || !incidentCategory || !incidentDescription.trim()) {
      setError('Veuillez remplir tous les champs obligatoires');
      return;
    }
    setIncidentSubmitting(true);
    setError('');
    try {
      const res = await api.post('/api/incidents', {
        package_id: incidentPackageId,
        category: incidentCategory,
        description: incidentDescription.slice(0, 1000),
      });

      // Upload photo if provided
      if (incidentPhoto && res.data?.id) {
        const formData = new FormData();
        formData.append('file', incidentPhoto);
        await api.post(`/api/incidents/${res.data.id}/photo`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          }
        });
      }

      // Notifier les parties concernées (in-app + WhatsApp)
      if (res.data?.id) {
        try {
          await api.post(`/api/incidents/${res.data.id}/notify`);
        } catch (notifyErr) {
          console.error('Incident notify failed:', notifyErr);
        }
      }

      setSuccess('Incident signalé avec succès');
      setShowIncidentDialog(false);
      fetchIncidents();
    } catch (err) {
      setError(extractErrorMessage(err, t('errorCreating')));
    } finally {
      setIncidentSubmitting(false);
    }
  };


  const shipmentStatusLabels = SHIPMENT_STATUS_LABELS[language] || SHIPMENT_STATUS_LABELS.fr;

  const getStatusColor = (status) => {
    const colors = {
      completed: 'bg-green-50 text-green-700 border border-green-200',
      delivered: 'bg-green-50 text-green-700 border border-green-200',
      ready_for_pickup: 'bg-blue-50 text-blue-700 border border-blue-200',
      arrived: 'bg-cyan-50 text-cyan-700 border border-cyan-200',
      in_transit: 'bg-purple-50 text-purple-700 border border-purple-200',
      delivering: 'bg-blue-50 text-blue-700 border border-blue-200',
      awaiting_delivery: 'bg-amber-50 text-amber-700 border border-amber-200',
    };
    return colors[status] || 'bg-gray-100 text-gray-600 border border-gray-200';
  };

  const getStatusLabel = (status) => {
    return shipmentStatusLabels[status] || status;
  };

  const fmt = (v) =>
    Number(v || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';

  // Local filtering of shipment packages by payment criteria
  const filteredShipmentPackages = useMemo(() => {
    if (!shipmentPackages) return null;

    const query = packageSearch.trim().toLowerCase();

    const matchesFilters = (pkg) => {
      if (filterPaymentStatus && (pkg.payment_status || 'unpaid') !== filterPaymentStatus) return false;
      if (filterPaymentMethod && (pkg.payment_method || '') !== filterPaymentMethod) return false;
      if (filterPaymentLocation && (pkg.payment_location || '') !== filterPaymentLocation) return false;
      if (query) {
        const match = (pkg.oce_code || '').toLowerCase().includes(query) ||
          (pkg.recipient_name || '').toLowerCase().includes(query) ||
          (pkg.recipient_phone || '').includes(query) ||
          (pkg.sender_name || '').toLowerCase().includes(query);
        if (!match) return false;
      }
      return true;
    };

    return {
      awaiting_customs: (shipmentPackages.awaiting_customs || []).filter(matchesFilters),
      ready_for_delivery: (shipmentPackages.ready_for_delivery || []).filter(matchesFilters),
      locked_for_delivery: (shipmentPackages.locked_for_delivery || []).filter(matchesFilters),
      at_relay_point: (shipmentPackages.at_relay_point || []).filter(matchesFilters),
      delivered_packages: (shipmentPackages.delivered_packages || []).filter(matchesFilters),
      other_packages: (shipmentPackages.other_packages || []).filter(matchesFilters),
    };
  }, [shipmentPackages, filterPaymentStatus, filterPaymentMethod, filterPaymentLocation, packageSearch]);

  // Colis prêts pour livraison regroupés par destinataire (téléphone/email/nom).
  // Permet au livreur de voir d'un coup tous les colis d'un même client et
  // combien il en reste à livrer — évite les allers-retours.
  const readyByRecipient = useMemo(() => {
    const list = filteredShipmentPackages?.ready_for_delivery || [];
    const groups = new Map();
    for (const p of list) {
      const key = (p.recipient_phone || p.recipient_email || p.recipient_name || '—').trim().toLowerCase();
      if (!groups.has(key)) {
        groups.set(key, {
          key,
          name: p.recipient_name || 'Client',
          phone: p.recipient_phone || '',
          packages: [],
        });
      }
      groups.get(key).packages.push(p);
    }
    return Array.from(groups.values());
  }, [filteredShipmentPackages]);

  // Villes du pays destination comme points relais
  const relayCities = useMemo(() => {
    if (!selectedShipment) return [];
    const shipment = shipments.find(s => s.id === selectedShipment);
    if (!shipment?.destination_country) return [];
    return getCitiesForCountry(shipment.destination_country);
  }, [selectedShipment, shipments, getCitiesForCountry]);

  const PackagePaymentInfo = ({ pkg }) => {
    const price = pkg.price || pkg.total_price || 0;
    if (!price) return null;
    const status = pkg.payment_status || 'unpaid';
    const advance = pkg.advance_amount || 0;
    const paid = status === 'paid' ? price : advance;
    const pct = price > 0 ? Math.min(100, Math.round((paid / price) * 100)) : 0;

    const statusConfig = {
      paid: { label: 'Payé', bg: 'bg-green-500', text: 'text-green-700', badge: 'bg-green-100 text-green-700' },
      advance: { label: 'Acompte', bg: 'bg-amber-500', text: 'text-amber-700', badge: 'bg-amber-100 text-amber-700' },
      unpaid: { label: 'Non payé', bg: 'bg-red-300', text: 'text-red-700', badge: 'bg-red-100 text-red-700' },
    };
    const cfg = statusConfig[status] || statusConfig.unpaid;

    return (
      <div className="mt-2 p-2 bg-muted/50 rounded-md">
        <div className="flex items-center justify-between text-xs mb-1">
          <span className={`font-medium ${cfg.text}`}>
            <DollarSign className="h-3 w-3 inline mr-0.5" />
            {cfg.label}
          </span>
          <span className="text-muted-foreground">{fmt(paid)} / {fmt(price)}</span>
        </div>
        <div className="h-2 bg-muted rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all ${cfg.bg}`}
            style={{ width: `${pct}%` }}
          />
        </div>
        {status !== 'paid' && price > paid && (
          <p className="text-[10px] text-red-600 mt-1 font-medium">
            Reste à collecter : {fmt(price - paid)}
          </p>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <ProfessionalLayout>
        <div className="flex items-center justify-center h-64">
          <p className="text-lg text-muted-foreground">Chargement...</p>
        </div>
      </ProfessionalLayout>
    );
  }

  // Statistiques rapides
  const totalPackages = shipments.reduce((sum, s) => sum + s.total_packages, 0);
  const deliveredPackages = shipments.reduce((sum, s) => sum + s.delivered_packages, 0);
  const pendingDelivery = totalPackages - deliveredPackages;
  const overallProgress = totalPackages > 0 ? (deliveredPackages / totalPackages) * 100 : 0;

  return (
    <ProfessionalLayout>
      <SEOHead title={t('seoDashboardTitle')} description={t('seoDashboardDesc')} noindex />
      <div className="space-y-6">
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {success && (
          <Alert className="bg-green-500/10 border-green-200">
            <AlertDescription className="text-green-800">{success}</AlertDescription>
          </Alert>
        )}

        {/* Statistiques globales */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
          <Card style={{ background: 'linear-gradient(135deg, #f0f4ff 0%, #e8ecf4 100%)', borderColor: '#c7d2e8' }}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Départs Actifs</CardTitle>
              <Truck className="h-4 w-4 text-blue-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{shipments.length}</div>
              <p className="text-xs text-muted-foreground">
                Départs à traiter
              </p>
            </CardContent>
          </Card>

          <Card style={{ background: 'linear-gradient(135deg, #fff5f0 0%, #ffe8d6 100%)', borderColor: '#f0c8a8' }}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Colis à Livrer</CardTitle>
              <Package className="h-4 w-4 text-orange-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{pendingDelivery}</div>
              <p className="text-xs text-muted-foreground">
                En attente de livraison
              </p>
            </CardContent>
          </Card>

          <Card style={{ background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)', borderColor: '#a7d8b8' }}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Colis Livrés</CardTitle>
              <CheckCircle className="h-4 w-4 text-green-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{deliveredPackages}</div>
              <p className="text-xs text-muted-foreground">
                Livraisons validées
              </p>
            </CardContent>
          </Card>

          <Card style={{ background: 'linear-gradient(135deg, #faf5ff 0%, #f0e4ff 100%)', borderColor: '#d4b8f0' }}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Progression</CardTitle>
              <Package2 className="h-4 w-4 text-purple-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{overallProgress.toFixed(1)}%</div>
              <Progress value={overallProgress} className="mt-2" />
            </CardContent>
          </Card>
        </div>

        {/* Barre de filtres */}
        <Card>
          <CardContent className="py-3 px-4">
            <div className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-3">
              <div className="flex items-center gap-1 text-sm font-medium text-muted-foreground">
                <Filter className="h-4 w-4" />
                Filtres
              </div>

              <GeographicFilter
                selectedCountry={filterCountry}
                selectedCity={filterCity}
                onCountryChange={setFilterCountry}
                onCityChange={setFilterCity}
              />

              <select
                value={filterPaymentStatus}
                onChange={(e) => setFilterPaymentStatus(e.target.value)}
                className="border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent"
              >
                <option value="">Paiement: Tous</option>
                <option value="paid">Paye</option>
                <option value="advance">Acompte</option>
                <option value="unpaid">Non paye</option>
              </select>

              <select
                value={filterPaymentMethod}
                onChange={(e) => setFilterPaymentMethod(e.target.value)}
                className="border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent"
              >
                <option value="">Methode: Toutes</option>
                <option value="cash">Especes</option>
                <option value="bank_transfer">Virement</option>
                <option value="mobile_money">Mobile Money</option>
                <option value="card">Carte</option>
              </select>

              <select
                value={filterPaymentLocation}
                onChange={(e) => setFilterPaymentLocation(e.target.value)}
                className="border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent"
              >
                <option value="">Lieu: Tous</option>
                <option value="origin">Origine</option>
                <option value="destination">Destination</option>
              </select>

              {hasActiveFilters && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={resetFilters}
                  className="text-muted-foreground hover:text-foreground"
                >
                  <X className="h-4 w-4 mr-1" />
                  Reinitialiser
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        <Tabs defaultValue="shipments" className="w-full">
          <TabsList className="flex flex-wrap h-auto gap-1">
            <TabsTrigger value="shipments">{t('shipments')}</TabsTrigger>
            <TabsTrigger value="scanner">Scanner</TabsTrigger>
            <TabsTrigger value="finance" onClick={() => fetchFinance()}>
              <Wallet className="h-4 w-4 mr-1" />
              Finance
            </TabsTrigger>
            <TabsTrigger value="history" onClick={() => { if (completedShipments.length === 0) fetchDeliveryHistory(); }}>{t('deliveryHistory')}</TabsTrigger>
            <TabsTrigger value="incidents">Incidents</TabsTrigger>
            {expenseFeatureEnabled && (
              <TabsTrigger value="expenses" onClick={() => { if (expenses.length === 0) fetchExpenses(); }}>
                <FileText className="h-4 w-4 mr-1" />
                {language === 'en' ? 'Expenses' : 'Notes de frais'}
              </TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="shipments" className="space-y-4">
            {shipments.length === 0 ? (
              <Card>
                <CardContent className="pt-6">
                  <div className="text-center py-8">
                    <Truck className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">Aucun départ à traiter</p>
                  </div>
                </CardContent>
              </Card>
            ) : (
              shipments.map(shipment => (
                <Card key={shipment.id}>
                  <CardHeader
                    className="cursor-pointer"
                    onClick={() => {
                      if (selectedShipment === shipment.id) {
                        setSelectedShipment(null);
                        setShipmentPackages(null);
                      } else {
                        fetchShipmentPackages(shipment.id);
                      }
                    }}
                  >
                    <div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                          <Truck className="h-5 w-5 text-accent shrink-0" />
                          <div className="min-w-0">
                            <CardTitle className="text-base sm:text-lg truncate">{shipment.name}</CardTitle>
                            <CardDescription className="mt-1">
                              <CountryRoute origin={shipment.origin_country} destination={shipment.destination_country} size="sm" />
                            </CardDescription>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <Badge className={`text-xs ${getStatusColor(shipment.status)}`}>
                            {getStatusLabel(shipment.status)}
                          </Badge>
                          {selectedShipment === shipment.id ? (
                            <ChevronUp className="h-5 w-5 text-muted-foreground" />
                          ) : (
                            <ChevronDown className="h-5 w-5 text-muted-foreground" />
                          )}
                        </div>
                      </div>

                      <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4">
                        <div className="text-center p-2 bg-muted rounded">
                          <p className="text-sm font-medium">{shipment.total_packages}</p>
                          <p className="text-xs text-muted-foreground">Total colis</p>
                        </div>
                        <div className="text-center p-2 bg-green-500/10 rounded">
                          <p className="text-sm font-medium text-green-700">{shipment.delivered_packages}</p>
                          <p className="text-xs text-muted-foreground">Livrés</p>
                        </div>
                        <div className="text-center p-2 bg-accent/10 rounded">
                          <p className="text-sm font-medium text-accent">{shipment.pending_delivery}</p>
                          <p className="text-xs text-muted-foreground">En attente</p>
                        </div>
                        <div className="text-center p-2 bg-yellow-500/10 rounded">
                          <p className="text-sm font-medium text-yellow-700">{shipment.delivery_progress}%</p>
                          <p className="text-xs text-muted-foreground">Progression</p>
                        </div>
                      </div>
                    </div>
                  </CardHeader>

                  {selectedShipment === shipment.id && filteredShipmentPackages && (
                    <CardContent>
                      <div className="space-y-6">
                        {/* Déchargement : scanner tous les colis du départ à l'arrivée.
                            Les colis embarqués non scannés pourront être signalés manquants. */}
                        <Button
                          type="button"
                          className="w-full bg-cyan-600 hover:bg-cyan-700"
                          onClick={() => setUnloadScan({ open: true, shipmentId: shipment.id })}
                        >
                          <Scan className="h-4 w-4 mr-2" />
                          {language === 'en' ? 'Unload (scan on arrival)' : 'Décharger (scan à l\'arrivée)'}
                        </Button>

                        {/* Recherche de colis */}
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                          <input
                            type="text"
                            value={packageSearch}
                            onChange={(e) => setPackageSearch(e.target.value)}
                            placeholder={language === 'en' ? 'Search by name, code, phone...' : 'Rechercher par nom, code, téléphone...'}
                            className="w-full pl-9 pr-8 py-2 border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-accent"
                          />
                          {packageSearch && (
                            <button onClick={() => setPackageSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2">
                              <X className="h-4 w-4 text-muted-foreground hover:text-foreground" />
                            </button>
                          )}
                        </div>

                        {/* Colis en attente de dédouanement (visibles mais non livrables) */}
                        {filteredShipmentPackages?.awaiting_customs && filteredShipmentPackages.awaiting_customs.length > 0 && (
                          <div>
                            <div className="flex items-center gap-2 mb-4">
                              <Clock className="h-4 w-4 text-orange-600" />
                              <h4 className="font-medium text-orange-700">
                                En attente de dédouanement ({filteredShipmentPackages.awaiting_customs.length})
                              </h4>
                            </div>
                            <div className="grid gap-3">
                              {filteredShipmentPackages.awaiting_customs.map(pkg => (
                                <div key={pkg.id} className="border-2 border-orange-200 bg-accent/10 rounded-lg p-4">
                                  <div className="flex gap-3">
                                    {pkg.photo_url ? (
                                      <div className="shrink-0 w-14 h-14 sm:w-16 sm:h-16 rounded-lg overflow-hidden bg-muted">
                                        <img src={pkg.photo_url} alt={pkg.oce_code} className="w-full h-full object-cover" />
                                      </div>
                                    ) : pkg.photo_count > 0 ? (
                                      <div className="shrink-0 w-14 h-14 sm:w-16 sm:h-16 rounded-lg bg-muted flex items-center justify-center">
                                        <Camera className="h-5 w-5 text-muted-foreground" />
                                      </div>
                                    ) : null}
                                    <div className="flex-1 min-w-0 flex items-center justify-between">
                                    <div className="flex-1 min-w-0">
                                      <p className="font-medium text-orange-700">{pkg.oce_code}</p>
                                      <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                                        <span className="flex items-center gap-1">
                                          <User className="h-3 w-3" />
                                          {pkg.recipient_name}
                                        </span>
                                        <span className="flex items-center gap-1">
                                          <Phone className="h-3 w-3" />
                                          {pkg.recipient_phone || 'N/A'}
                                        </span>
                                        <span>{pkg.weight} kg</span>
                                      </div>
                                      <p className="text-xs text-muted-foreground mt-1">{pkg.description}</p>
                                    </div>

                                    <div className="flex flex-col items-end gap-2">
                                      <Badge className="bg-orange-500">
                                        En attente dédouanement
                                      </Badge>
                                      <div className="flex items-center gap-2">
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          className="text-orange-500 hover:text-orange-700 h-7 px-2"
                                          onClick={(e) => { e.stopPropagation(); openIncidentDialog(pkg.id); }}
                                          title="Signaler un incident"
                                        >
                                          <AlertTriangle className="h-3 w-3" />
                                        </Button>
                                        <p className="text-xs text-orange-600 italic">
                                          Non livrable
                                        </p>
                                      </div>
                                    </div>
                                  </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Colis verrouillés : à décharger avant livraison */}
                        {filteredShipmentPackages?.locked_for_delivery && filteredShipmentPackages.locked_for_delivery.length > 0 && (
                          <div>
                            <div className="flex items-center gap-2 mb-4">
                              <Lock className="h-4 w-4 text-slate-500" />
                              <h4 className="font-medium text-slate-700">
                                À décharger avant livraison ({filteredShipmentPackages.locked_for_delivery.length})
                              </h4>
                            </div>
                            <div className="rounded-md bg-slate-50 border border-slate-200 text-slate-600 text-xs px-3 py-2 mb-3">
                              Ces colis ne sont pas encore livrables. Déchargez-les — via le <strong>scan à l'arrivée</strong> ou le bouton <strong>Décharger</strong> ci-dessous — pour confirmer leur réception et les débloquer.
                            </div>
                            <div className="grid gap-3">
                              {filteredShipmentPackages.locked_for_delivery.map(pkg => (
                                <div key={pkg.id} className="border-2 border-slate-200 bg-slate-50 rounded-lg p-4 opacity-90">
                                  <div className="flex items-center gap-3">
                                    <Lock className="h-4 w-4 text-slate-400 shrink-0" />
                                    <div className="flex-1 min-w-0">
                                      <p className="font-medium text-slate-700">{pkg.oce_code}</p>
                                      <div className="flex flex-wrap items-center gap-2 sm:gap-4 mt-1 text-sm text-muted-foreground">
                                        <span className="flex items-center gap-1"><User className="h-3 w-3" />{pkg.recipient_name}</span>
                                        <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{pkg.recipient_phone || 'N/A'}</span>
                                        <span>{pkg.weight} kg</span>
                                      </div>
                                    </div>
                                    <Button
                                      size="sm"
                                      className="bg-cyan-600 hover:bg-cyan-700 shrink-0 h-8"
                                      disabled={unloadingPkg === pkg.id}
                                      onClick={() => handleUnloadPackage(pkg)}
                                    >
                                      {unloadingPkg === pkg.id ? (
                                        <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
                                      ) : (
                                        <PackageCheck className="h-3 w-3 mr-1" />
                                      )}
                                      Décharger
                                    </Button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Colis prêts pour livraison */}
                        <div>
                          <div className="flex items-center gap-2 mb-4">
                            <Package className="h-4 w-4 text-indigo-600" />
                            <h4 className="font-medium text-indigo-700">
                              Prêts pour livraison ({filteredShipmentPackages?.ready_for_delivery?.length || 0})
                            </h4>
                          </div>

                          {!filteredShipmentPackages?.ready_for_delivery || filteredShipmentPackages.ready_for_delivery.length === 0 ? (
                            <div className="text-center py-4 text-muted-foreground border-2 border-dashed rounded-lg">
                              <AlertCircle className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                              <p>Aucun colis prêt pour livraison</p>
                              <p className="text-xs mt-1">En attente de dédouanement par l'admin</p>
                            </div>
                          ) : (
                            <div className="space-y-4">
                              {readyByRecipient.map(group => (
                                <div key={group.key} className="rounded-lg border border-indigo-200 overflow-hidden">
                                  <div className="flex items-center justify-between gap-2 bg-indigo-100/60 px-3 py-2">
                                    <div className="min-w-0">
                                      <p className="font-semibold text-sm text-indigo-900 truncate">{group.name}</p>
                                      {group.phone && (
                                        <p className="text-xs text-indigo-700 flex items-center gap-1">
                                          <Phone className="h-3 w-3" />{group.phone}
                                        </p>
                                      )}
                                    </div>
                                    <Badge className="bg-indigo-600 text-white shrink-0">
                                      Reste {group.packages.length} à livrer
                                    </Badge>
                                  </div>
                                  <div className="grid gap-3 p-3">
                                    {group.packages.map(pkg => (
                                      <div key={pkg.id} className="border-2 border-indigo-200 bg-indigo-50 rounded-lg p-4">
                                        <div className="flex gap-3">
                                    {pkg.photo_url ? (
                                      <div className="shrink-0 w-14 h-14 sm:w-16 sm:h-16 rounded-lg overflow-hidden bg-muted">
                                        <img src={pkg.photo_url} alt={pkg.oce_code} className="w-full h-full object-cover" />
                                      </div>
                                    ) : pkg.photo_count > 0 ? (
                                      <div className="shrink-0 w-14 h-14 sm:w-16 sm:h-16 rounded-lg bg-muted flex items-center justify-center">
                                        <Camera className="h-5 w-5 text-muted-foreground" />
                                      </div>
                                    ) : null}
                                    <div className="flex-1 min-w-0 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                                    <div className="flex-1 min-w-0">
                                      <p className="font-medium text-indigo-700">{pkg.oce_code}</p>
                                      <div className="flex flex-wrap items-center gap-2 sm:gap-4 mt-2 text-sm text-muted-foreground">
                                        <span className="flex items-center gap-1">
                                          <User className="h-3 w-3" />
                                          {pkg.recipient_name}
                                        </span>
                                        <span className="flex items-center gap-1">
                                          <Phone className="h-3 w-3" />
                                          {pkg.recipient_phone || 'N/A'}
                                        </span>
                                        <span>{pkg.weight} kg</span>
                                      </div>
                                      <p className="text-xs text-muted-foreground mt-1">{pkg.description}</p>
                                      <PackagePaymentInfo pkg={pkg} />
                                    </div>

                                    <div className="flex flex-col items-start sm:items-end gap-2">
                                      <div className="flex items-center gap-2">
                                        <Badge className={pkg.status === 'out_for_delivery' ? 'bg-amber-500 text-white' : 'bg-indigo-500 text-white'}>
                                          {pkg.status === 'out_for_delivery' ? 'En livraison' : 'À livrer'}
                                        </Badge>
                                      </div>
                                      <div className="flex flex-wrap items-center gap-2">
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          className="text-orange-500 hover:text-orange-700 h-7 px-2"
                                          onClick={(e) => { e.stopPropagation(); openIncidentDialog(pkg.id); }}
                                          title="Signaler un incident"
                                        >
                                          <AlertTriangle className="h-3 w-3" />
                                        </Button>
                                        {(pkg.payment_status !== 'paid') && (pkg.price || pkg.total_price) ? (
                                          <Button
                                            size="sm"
                                            variant="outline"
                                            className="text-emerald-600 hover:text-emerald-800 h-7 px-2"
                                            onClick={(e) => { e.stopPropagation(); openPaymentDialog(pkg); }}
                                            title="Enregistrer un paiement"
                                          >
                                            <Banknote className="h-3 w-3 mr-1" />
                                            Collecter
                                          </Button>
                                        ) : null}
                                        {pkg.status !== 'out_for_delivery' && (
                                          <Button
                                            size="sm"
                                            variant="outline"
                                            className="text-blue-600 hover:text-blue-800 h-7 px-2"
                                            onClick={(e) => { e.stopPropagation(); handleStartDelivery(pkg); }}
                                            disabled={startingDelivery === pkg.id}
                                            title="Partir en livraison — envoie le code au client"
                                          >
                                            {startingDelivery === pkg.id ? (
                                              <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
                                            ) : (
                                              <Truck className="h-3 w-3 mr-1" />
                                            )}
                                            Partir
                                          </Button>
                                        )}
                                        <Button
                                          size="sm"
                                          onClick={(e) => { e.stopPropagation(); openDeliveryFlow(pkg); }}
                                          className="bg-green-600 hover:bg-green-700"
                                        >
                                          <CheckCircle className="h-3 w-3 mr-1" />
                                          Livrer
                                        </Button>
                                      </div>
                                    </div>
                                  </div>
                                  </div>
                                </div>
                                    ))}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* Colis en point relais (ready_for_pickup) */}
                        {filteredShipmentPackages?.at_relay_point && filteredShipmentPackages.at_relay_point.length > 0 && (
                          <div>
                            <div className="flex items-center gap-2 mb-4">
                              <MapPin className="h-4 w-4 text-blue-600" />
                              <h4 className="font-medium text-blue-700">
                                {t('atRelayPoint')} ({filteredShipmentPackages.at_relay_point.length})
                              </h4>
                            </div>
                            <div className="grid gap-3">
                              {filteredShipmentPackages.at_relay_point.map(pkg => (
                                <div key={pkg.id} className="border-2 border-blue-200 bg-blue-50 rounded-lg p-4">
                                  <div className="flex gap-3">
                                    {pkg.photo_url ? (
                                      <div className="shrink-0 w-14 h-14 sm:w-16 sm:h-16 rounded-lg overflow-hidden bg-muted">
                                        <img src={pkg.photo_url} alt={pkg.oce_code} className="w-full h-full object-cover" />
                                      </div>
                                    ) : pkg.photo_count > 0 ? (
                                      <div className="shrink-0 w-14 h-14 sm:w-16 sm:h-16 rounded-lg bg-muted flex items-center justify-center">
                                        <Camera className="h-5 w-5 text-muted-foreground" />
                                      </div>
                                    ) : null}
                                    <div className="flex-1 min-w-0 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                                    <div className="flex-1 min-w-0">
                                      <p className="font-medium text-blue-700">{pkg.oce_code}</p>
                                      <div className="flex flex-wrap items-center gap-2 sm:gap-4 mt-2 text-sm text-muted-foreground">
                                        <span className="flex items-center gap-1">
                                          <User className="h-3 w-3" />
                                          {pkg.recipient_name}
                                        </span>
                                        <span className="flex items-center gap-1">
                                          <Phone className="h-3 w-3" />
                                          {pkg.recipient_phone || 'N/A'}
                                        </span>
                                        <span>{pkg.weight} kg</span>
                                      </div>
                                      <p className="text-xs text-muted-foreground mt-1">{pkg.description}</p>
                                    </div>
                                    <div className="flex flex-col items-start sm:items-end gap-2">
                                      <Badge className="bg-blue-500 text-white">
                                        <MapPin className="h-3 w-3 mr-1" />
                                        {t('atRelayPoint')} — {pkg.relay_point_info?.name || t('relayPoint')}
                                      </Badge>
                                      {pkg.relay_point_info && (
                                        <p className="text-xs text-blue-600">
                                          {pkg.relay_point_info.address}, {pkg.relay_point_info.city}
                                        </p>
                                      )}
                                    </div>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {filteredShipmentPackages?.delivered_packages && filteredShipmentPackages.delivered_packages.length > 0 && (
                          <div className="mt-6">
                            <div className="flex items-center gap-2 mb-4">
                              <CheckCircle className="h-4 w-4 text-green-600" />
                              <h4 className="font-medium text-green-700">Colis livrés ({filteredShipmentPackages.delivered_packages.length})</h4>
                            </div>

                            <div className="grid gap-3">
                              {(filteredShipmentPackages.delivered_packages || []).map(pkg => {
                                const deliveryInfo = pkg.delivery_info || {};
                                const deliveredAt = deliveryInfo.delivered_at || deliveryInfo.deposited_at || pkg.updated_at;
                                return (
                                  <div key={pkg.id} className="border-2 border-green-200 bg-green-50 rounded-lg p-4">
                                    <div className="flex gap-3">
                                      {pkg.photo_url ? (
                                        <div className="shrink-0 w-14 h-14 sm:w-16 sm:h-16 rounded-lg overflow-hidden bg-muted">
                                          <img src={pkg.photo_url} alt={pkg.oce_code} className="w-full h-full object-cover" />
                                        </div>
                                      ) : pkg.photo_count > 0 ? (
                                        <div className="shrink-0 w-14 h-14 sm:w-16 sm:h-16 rounded-lg bg-muted flex items-center justify-center">
                                          <Camera className="h-5 w-5 text-muted-foreground" />
                                        </div>
                                      ) : null}
                                      <div className="flex-1 min-w-0 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                                      <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                                          <span className="font-medium text-green-800">{pkg.oce_code}</span>
                                          <Badge className="bg-green-600 text-white text-xs">
                                            <CheckCircle className="h-3 w-3 mr-1" />
                                            Livré
                                          </Badge>
                                          {(pkg.delivery_type || deliveryInfo.delivery_type) && (
                                            <Badge variant="outline" className="text-xs">
                                              {(pkg.delivery_type || deliveryInfo.delivery_type) === 'relay_point'
                                                ? (language === 'en' ? 'Relay point' : 'Point relais')
                                                : (language === 'en' ? 'In hand' : 'Main propre')}
                                            </Badge>
                                          )}
                                        </div>
                                        <div className="flex flex-wrap items-center gap-2 sm:gap-4 mt-2 text-sm text-muted-foreground">
                                          <span className="flex items-center gap-1">
                                            <User className="h-3 w-3" />
                                            {pkg.recipient_name}
                                          </span>
                                          <span className="flex items-center gap-1">
                                            <Phone className="h-3 w-3" />
                                            {pkg.recipient_phone || 'N/A'}
                                          </span>
                                          <span>{pkg.weight} kg</span>
                                        </div>
                                        {deliveredAt && (
                                          <p className="text-xs text-green-700 mt-1 flex items-center gap-1">
                                            <Clock className="h-3 w-3" />
                                            Livré le {new Date(deliveredAt).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', {
                                              day: '2-digit', month: 'short', year: 'numeric',
                                              hour: '2-digit', minute: '2-digit'
                                            })}
                                          </p>
                                        )}
                                        {deliveryInfo.delivery_notes && (
                                          <p className="text-xs text-muted-foreground mt-1 italic">
                                            {deliveryInfo.delivery_notes}
                                          </p>
                                        )}
                                        <PackagePaymentInfo pkg={pkg} />
                                      </div>
                                      {pkg.payment_status !== 'paid' && (pkg.price || pkg.total_price) ? (
                                        <div className="flex items-center">
                                          <Button
                                            size="sm"
                                            variant="outline"
                                            className="text-emerald-600 hover:text-emerald-800 h-7 px-2"
                                            onClick={(e) => { e.stopPropagation(); openPaymentDialog(pkg); }}
                                            title="Enregistrer un paiement"
                                          >
                                            <Banknote className="h-3 w-3 mr-1" />
                                            Collecter
                                          </Button>
                                        </div>
                                      ) : null}
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                        {/* Autres colis (en transit, arrivés, etc.) */}
                        {filteredShipmentPackages?.other_packages && filteredShipmentPackages.other_packages.length > 0 && (
                          <div className="mt-6">
                            <div className="flex items-center gap-2 mb-4">
                              <Truck className="h-4 w-4 text-gray-500" />
                              <h4 className="font-medium text-gray-600">
                                En cours d'acheminement ({filteredShipmentPackages.other_packages.length})
                              </h4>
                            </div>
                            <div className="grid gap-3">
                              {filteredShipmentPackages.other_packages.map(pkg => (
                                <div key={pkg.id} className="border border-border bg-muted/30 rounded-lg p-4">
                                  <div className="flex gap-3">
                                    {pkg.photo_url ? (
                                      <div className="shrink-0 w-14 h-14 sm:w-16 sm:h-16 rounded-lg overflow-hidden bg-muted">
                                        <img src={pkg.photo_url} alt={pkg.oce_code} className="w-full h-full object-cover" />
                                      </div>
                                    ) : pkg.photo_count > 0 ? (
                                      <div className="shrink-0 w-14 h-14 sm:w-16 sm:h-16 rounded-lg bg-muted flex items-center justify-center">
                                        <Camera className="h-5 w-5 text-muted-foreground" />
                                      </div>
                                    ) : null}
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                                        <span className="font-medium">{pkg.oce_code}</span>
                                        <Badge variant="secondary" className="text-xs">
                                          {pkg.status === 'arrived' ? 'Arrivé' :
                                           pkg.status === 'in_transit' ? 'En transit' :
                                           pkg.status === 'registered' ? 'Enregistré' :
                                           pkg.status === 'collected' ? 'Collecté' :
                                           pkg.status?.replace(/_/g, ' ')}
                                        </Badge>
                                      </div>
                                      <div className="flex flex-wrap items-center gap-2 sm:gap-4 mt-2 text-sm text-muted-foreground">
                                        <span className="flex items-center gap-1">
                                          <User className="h-3 w-3" />
                                          {pkg.recipient_name}
                                        </span>
                                        <span>{pkg.weight} kg</span>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  )}
                </Card>
              ))
            )}
          </TabsContent>

          <TabsContent value="scanner">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Scan className="h-5 w-5" />
                  Scanner de Livraison Rapide
                </CardTitle>
                <CardDescription>
                  Saisissez le code OCE pour valider rapidement une livraison
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={async (e) => {
                  e.preventDefault();
                  setError('');
                  try {
                    await api.post('/api/local-partner/validate-delivery', {
                      oce_code: scannerOceCode,
                      delivery_notes: scannerNotes,
                      verification_code: scannerCode || undefined,
                      recipient_signature: '',
                      delivery_photo: '',
                    });
                    // Message enrichi (colis restants du même client) si le colis
                    // appartient au départ sélectionné ; sinon message simple.
                    const scannedPkg = shipmentPackages
                      ? [...(shipmentPackages.ready_for_delivery || []), ...(shipmentPackages.locked_for_delivery || []), ...(shipmentPackages.at_relay_point || [])]
                          .find(p => p.oce_code === scannerOceCode)
                      : null;
                    setSuccess(scannedPkg ? deliverySuccessMsg(scannedPkg) : `Livraison du colis ${scannerOceCode} validée !`);
                    setScannerOceCode('');
                    setScannerNotes('');
                    setScannerCode('');
                    fetchShipments();
                    if (selectedShipment) fetchShipmentPackages(selectedShipment);
                  } catch (err) {
                    setError(extractErrorMessage(err, t('errorStatusChange')));
                  }
                }} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Code OCE *
                    </label>
                    <input
                      type="text"
                      required
                      value={scannerOceCode}
                      onChange={(e) => setScannerOceCode(e.target.value.toUpperCase())}
                      className="w-full p-3 text-base sm:text-lg font-mono border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-accent"
                      placeholder="OCE123456789"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Code de livraison
                    </label>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={scannerCode}
                      onChange={(e) => setScannerCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      className="w-full p-3 text-base sm:text-lg font-mono tracking-widest border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-accent"
                      placeholder="123456"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Code à 6 chiffres reçu par le destinataire (WhatsApp/email). Requis pour débloquer la livraison.
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Notes de livraison
                    </label>
                    <textarea
                      value={scannerNotes}
                      onChange={(e) => setScannerNotes(e.target.value)}
                      className="w-full p-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-accent"
                      rows={3}
                      placeholder="Notes concernant la livraison (optionnel)..."
                    />
                  </div>

                  <Button type="submit" className="w-full bg-green-600 hover:bg-green-700">
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Valider la Livraison
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ============ FINANCE TAB ============ */}
          <TabsContent value="finance" className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Wallet className="h-5 w-5" />
                Suivi des paiements
              </h3>
              <Button size="sm" variant="outline" onClick={fetchFinance} disabled={financeLoading}>
                <RefreshCw className={`h-4 w-4 mr-1 ${financeLoading ? 'animate-spin' : ''}`} />
                {t('refresh')}
              </Button>
            </div>

            {financeLoading ? (
              <div className="flex justify-center py-8">
                <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : !financeData ? (
              <Card>
                <CardContent className="pt-6">
                  <div className="text-center py-8">
                    <Wallet className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">Chargement des données financières...</p>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <>
                {/* KPI Cards */}
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
                  <Card style={{ background: 'linear-gradient(135deg, #f0f4ff 0%, #e8ecf4 100%)', borderColor: '#c7d2e8' }}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <span className="text-[11px] text-muted-foreground font-semibold uppercase tracking-wider">Total colis</span>
                        <Package className="h-4 w-4 text-blue-500" />
                      </div>
                      <div className="text-2xl font-bold">{financeData.stats.total_packages}</div>
                      <p className="text-xs text-muted-foreground mt-1">{financeData.stats.my_deliveries} livrés par moi</p>
                    </CardContent>
                  </Card>

                  <Card style={{ background: 'linear-gradient(135deg, #fff5f0 0%, #ffe8d6 100%)', borderColor: '#f0c8a8' }}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <span className="text-[11px] text-muted-foreground font-semibold uppercase tracking-wider">Chiffre d'affaires</span>
                        <DollarSign className="h-4 w-4 text-emerald-500" />
                      </div>
                      <div className="text-2xl font-bold">{fmt(financeData.stats.total_revenue)}</div>
                    </CardContent>
                  </Card>

                  <Card style={{ background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)', borderColor: '#a7d8b8' }}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <span className="text-[11px] text-emerald-700 font-semibold uppercase tracking-wider">Collecté par moi</span>
                        <CreditCard className="h-4 w-4 text-emerald-600" />
                      </div>
                      <div className="text-2xl font-bold text-emerald-700">{fmt(financeData.stats.my_collections_total || 0)}</div>
                      <p className="text-xs text-emerald-600 mt-1">{financeData.stats.my_collections_count || 0} encaissements</p>
                    </CardContent>
                  </Card>

                  <Card style={{ background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)', borderColor: '#a7d8b8' }}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <span className="text-[11px] text-muted-foreground font-semibold uppercase tracking-wider">Encaissé total</span>
                        <CheckCircle className="h-4 w-4 text-green-500" />
                      </div>
                      <div className="text-2xl font-bold text-green-700">{fmt(financeData.stats.paid_revenue)}</div>
                      <p className="text-xs text-muted-foreground mt-1">{financeData.stats.paid_count} payés</p>
                    </CardContent>
                  </Card>

                  <Card style={{ background: 'linear-gradient(135deg, #fef2f2 0%, #fecaca 100%)', borderColor: '#f0a8a8' }}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <span className="text-[11px] text-muted-foreground font-semibold uppercase tracking-wider">Restant dû</span>
                        <AlertCircle className="h-4 w-4 text-red-500" />
                      </div>
                      <div className="text-2xl font-bold text-red-700">{fmt(financeData.stats.outstanding)}</div>
                      <p className="text-xs text-muted-foreground mt-1">{financeData.stats.unpaid_count} impayés</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Payment progress global */}
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium">Progression globale des paiements</span>
                      <span className="text-sm text-muted-foreground">
                        {financeData.stats.total_revenue > 0
                          ? `${Math.round(((financeData.stats.paid_revenue + financeData.stats.advance_revenue) / financeData.stats.total_revenue) * 100)}%`
                          : '0%'}
                      </span>
                    </div>
                    <div className="h-3 bg-muted rounded-full overflow-hidden flex">
                      {financeData.stats.total_revenue > 0 && (
                        <>
                          <div
                            className="h-full bg-green-500 transition-all"
                            style={{ width: `${(financeData.stats.paid_revenue / financeData.stats.total_revenue) * 100}%` }}
                          />
                          <div
                            className="h-full bg-amber-500 transition-all"
                            style={{ width: `${(financeData.stats.advance_revenue / financeData.stats.total_revenue) * 100}%` }}
                          />
                        </>
                      )}
                    </div>
                    <div className="flex gap-4 mt-2 text-xs">
                      <span className="flex items-center gap-1">
                        <span className="w-2.5 h-2.5 rounded-sm bg-green-500" /> Payé ({fmt(financeData.stats.paid_revenue)})
                      </span>
                      <span className="flex items-center gap-1">
                        <span className="w-2.5 h-2.5 rounded-sm bg-amber-500" /> Acompte ({fmt(financeData.stats.advance_revenue)})
                      </span>
                      <span className="flex items-center gap-1">
                        <span className="w-2.5 h-2.5 rounded-sm bg-red-300" /> Impayé ({fmt(financeData.stats.outstanding)})
                      </span>
                    </div>
                  </CardContent>
                </Card>

                {/* My Collections Summary */}
                {financeData.my_collections && financeData.my_collections.length > 0 && (
                  <Card>
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-lg flex items-center gap-2">
                          <CreditCard className="h-5 w-5 text-emerald-600" />
                          Mes encaissements
                        </CardTitle>
                        <Badge className="bg-emerald-100 text-emerald-700 text-xs">
                          {financeData.stats.my_collections_count} collectes — {fmt(financeData.stats.my_collections_total)}
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm min-w-[450px]">
                          <thead className="bg-muted/50">
                            <tr>
                              <th className="text-left px-3 py-2 font-medium text-muted-foreground">Code OCE</th>
                              <th className="text-left px-3 py-2 font-medium text-muted-foreground">Destinataire</th>
                              <th className="text-right px-3 py-2 font-medium text-muted-foreground">Montant</th>
                              <th className="text-center px-3 py-2 font-medium text-muted-foreground">Méthode</th>
                              <th className="text-left px-3 py-2 font-medium text-muted-foreground">Date</th>
                              <th className="text-left px-3 py-2 font-medium text-muted-foreground">Note</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-border">
                            {financeData.my_collections.map((c, idx) => {
                              const methodLabels = { cash: 'Espèces', mobile_money: 'Mobile Money', bank_transfer: 'Virement', card: 'Carte', other: 'Autre' };
                              return (
                                <tr key={idx} className="hover:bg-muted/30">
                                  <td className="px-3 py-2 font-mono text-xs">{c.oce_code}</td>
                                  <td className="px-3 py-2">{c.recipient_name}</td>
                                  <td className="px-3 py-2 text-right font-semibold text-emerald-700">{fmt(c.amount)}</td>
                                  <td className="px-3 py-2 text-center">
                                    <Badge variant="outline" className="text-[10px]">{methodLabels[c.method] || c.method}</Badge>
                                  </td>
                                  <td className="px-3 py-2 text-xs text-muted-foreground">
                                    {c.collected_at ? new Date(c.collected_at).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', {
                                      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
                                    }) : '—'}
                                  </td>
                                  <td className="px-3 py-2 text-xs text-muted-foreground truncate max-w-[100px] sm:max-w-[150px]">{c.note || '—'}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Packages table */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Détail par colis</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {financeData.packages.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        <Package className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                        <p>Aucun colis à afficher</p>
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm min-w-[450px]">
                          <thead className="bg-muted/50">
                            <tr>
                              <th className="text-left px-3 py-2 font-medium text-muted-foreground">Code</th>
                              <th className="text-left px-3 py-2 font-medium text-muted-foreground">Destinataire</th>
                              <th className="text-right px-3 py-2 font-medium text-muted-foreground">Montant</th>
                              <th className="text-center px-3 py-2 font-medium text-muted-foreground">Paiement</th>
                              <th className="text-center px-3 py-2 font-medium text-muted-foreground">Progression</th>
                              <th className="text-center px-3 py-2 font-medium text-muted-foreground">Action</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-border">
                            {financeData.packages.map(pkg => {
                              const price = pkg.price || pkg.total_price || 0;
                              const pStatus = pkg.payment_status || 'unpaid';
                              const advance = pkg.advance_amount || 0;
                              const paid = pStatus === 'paid' ? price : advance;
                              const pct = price > 0 ? Math.min(100, Math.round((paid / price) * 100)) : 0;

                              const statusCfg = {
                                paid: { label: 'Payé', cls: 'bg-green-100 text-green-700' },
                                advance: { label: 'Acompte', cls: 'bg-amber-100 text-amber-700' },
                                unpaid: { label: 'Non payé', cls: 'bg-red-100 text-red-700' },
                              };
                              const sc = statusCfg[pStatus] || statusCfg.unpaid;

                              return (
                                <tr key={pkg.id} className="hover:bg-muted/30">
                                  <td className="px-3 py-2 font-mono text-xs">{pkg.oce_code}</td>
                                  <td className="px-3 py-2">{pkg.recipient_name}</td>
                                  <td className="px-3 py-2 text-right font-medium">{price > 0 ? fmt(price) : '—'}</td>
                                  <td className="px-3 py-2 text-center">
                                    <Badge className={`text-[10px] ${sc.cls}`}>{sc.label}</Badge>
                                  </td>
                                  <td className="px-3 py-2">
                                    <div className="flex items-center gap-2 min-w-[80px]">
                                      <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                                        <div
                                          className={`h-full rounded-full ${pct >= 100 ? 'bg-green-500' : pct > 0 ? 'bg-amber-500' : 'bg-red-300'}`}
                                          style={{ width: `${pct}%` }}
                                        />
                                      </div>
                                      <span className="text-xs text-muted-foreground w-8 text-right">{pct}%</span>
                                    </div>
                                  </td>
                                  <td className="px-3 py-2 text-center">
                                    {pStatus !== 'paid' && price > 0 ? (
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        className="text-emerald-600 hover:text-emerald-800 h-7 px-2"
                                        onClick={() => openPaymentDialog(pkg)}
                                      >
                                        <Banknote className="h-3 w-3 mr-1" />
                                        Collecter
                                      </Button>
                                    ) : price > 0 ? (
                                      <span className="text-xs text-green-600 font-medium">Solde</span>
                                    ) : (
                                      <span className="text-xs text-muted-foreground">—</span>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </>
            )}
          </TabsContent>

          {/* ============ HISTORY TAB ============ */}
          <TabsContent value="history" className="space-y-4">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <History className="h-5 w-5" />
                {t('deliveryHistory')}
              </h3>
              <Button size="sm" variant="outline" onClick={fetchDeliveryHistory} disabled={historyLoading}>
                <RefreshCw className={`h-4 w-4 mr-1 ${historyLoading ? 'animate-spin' : ''}`} />
                {t('refresh')}
              </Button>
            </div>

            {/* Search bar */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input
                type="text"
                value={historySearch}
                onChange={(e) => setHistorySearch(e.target.value)}
                placeholder="Rechercher un colis (code, nom, téléphone)..."
                className="w-full pl-9 pr-8 py-2 border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-accent"
              />
              {historySearch && (
                <button onClick={() => setHistorySearch('')} className="absolute right-2 top-1/2 -translate-y-1/2">
                  <X className="h-4 w-4 text-muted-foreground hover:text-foreground" />
                </button>
              )}
            </div>

            {historyLoading ? (
              <div className="flex justify-center py-8">
                <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : completedShipments.length === 0 ? (
              <Card>
                <CardContent className="pt-6">
                  <div className="text-center py-8">
                    <History className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">{t('noDeliveryHistory')}</p>
                  </div>
                </CardContent>
              </Card>
            ) : (
              completedShipments.map(shipment => {
                const isExpanded = expandedHistoryShipment === shipment.id;
                const shipPkgs = (shipment.packages || []).filter(pkg => {
                  if (!historySearch) return true;
                  const q = historySearch.toLowerCase();
                  return (pkg.oce_code || '').toLowerCase().includes(q) ||
                    (pkg.recipient_name || '').toLowerCase().includes(q) ||
                    (pkg.recipient_phone || '').includes(q) ||
                    (pkg.sender_name || '').toLowerCase().includes(q);
                });
                const matchesSearch = !historySearch ||
                  shipment.name?.toLowerCase().includes(historySearch.toLowerCase()) ||
                  shipPkgs.length > 0;
                if (!matchesSearch) return null;
                return (
                  <Card key={shipment.id}>
                    <CardHeader
                      className="cursor-pointer"
                      onClick={() => setExpandedHistoryShipment(isExpanded ? null : shipment.id)}
                    >
                      <div>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3 min-w-0 flex-1">
                            <Truck className="h-5 w-5 text-green-600 shrink-0" />
                            <div className="min-w-0">
                              <CardTitle className="text-base sm:text-lg truncate">{shipment.name}</CardTitle>
                              <CardDescription className="mt-1">
                                <CountryRoute origin={shipment.origin_country} destination={shipment.destination_country} size="sm" />
                              </CardDescription>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <Badge className="bg-green-600 text-white text-xs">
                              <CheckCircle className="h-3 w-3 mr-1" />
                              Livraison terminée
                            </Badge>
                            {isExpanded ? (
                              <ChevronUp className="h-5 w-5 text-muted-foreground" />
                            ) : (
                              <ChevronDown className="h-5 w-5 text-muted-foreground" />
                            )}
                          </div>
                        </div>

                        <div className="mt-3 grid grid-cols-2 gap-2">
                          <div className="text-center p-2 bg-green-500/10 rounded">
                            <p className="text-sm font-medium text-green-700">{shipment.total_packages}</p>
                            <p className="text-xs text-muted-foreground">Colis livrés</p>
                          </div>
                          <div className="text-center p-2 bg-green-500/10 rounded">
                            <p className="text-sm font-medium text-green-700">100%</p>
                            <p className="text-xs text-muted-foreground">Progression</p>
                          </div>
                        </div>
                      </div>
                    </CardHeader>

                    {isExpanded && (
                      <CardContent>
                        <div className="space-y-4">
                          {shipPkgs.length === 0 ? (
                            <div className="text-center py-4 text-muted-foreground text-sm">
                              {historySearch ? 'Aucun colis trouvé' : 'Aucun colis'}
                            </div>
                          ) : (
                            <div className="grid gap-3">
                              {shipPkgs.map(pkg => {
                                const isDeposit = pkg.status === 'ready_for_pickup';
                                const deliveryDate = pkg.delivery_info?.delivered_at || pkg.delivery_info?.deposited_at || pkg.updated_at;
                                return (
                                  <div key={pkg.id} className="border border-green-200 bg-green-50/50 rounded-lg p-3 sm:p-4">
                                    <div className="flex gap-3">
                                      {pkg.photo_url ? (
                                        <div className="shrink-0 w-14 h-14 sm:w-16 sm:h-16 rounded-lg overflow-hidden bg-muted">
                                          <img src={pkg.photo_url} alt={pkg.oce_code} className="w-full h-full object-cover" />
                                        </div>
                                      ) : pkg.photo_count > 0 ? (
                                        <div className="shrink-0 w-14 h-14 sm:w-16 sm:h-16 rounded-lg bg-muted flex items-center justify-center">
                                          <Camera className="h-5 w-5 text-muted-foreground" />
                                        </div>
                                      ) : null}
                                      <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                                          <span className="font-mono text-sm font-medium">{pkg.oce_code}</span>
                                          {isDeposit ? (
                                            <Badge className="bg-blue-500 text-white text-xs">
                                              <MapPin className="h-3 w-3 mr-1" />
                                              Point relais
                                            </Badge>
                                          ) : (
                                            <Badge className="bg-green-500 text-white text-xs">
                                              <CheckCircle className="h-3 w-3 mr-1" />
                                              Livré
                                            </Badge>
                                          )}
                                        </div>
                                        <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-sm text-muted-foreground">
                                          <span className="flex items-center gap-1">
                                            <User className="h-3 w-3" />
                                            {pkg.recipient_name}
                                          </span>
                                          <span className="flex items-center gap-1">
                                            <Phone className="h-3 w-3" />
                                            {pkg.recipient_phone || 'N/A'}
                                          </span>
                                          {pkg.weight && <span>{pkg.weight} kg</span>}
                                        </div>
                                        {isDeposit && pkg.relay_point_info && (
                                          <p className="text-xs text-blue-600 mt-1 flex items-center gap-1">
                                            <MapPin className="h-3 w-3" />
                                            {pkg.relay_point_info.name} — {pkg.relay_point_info.city}
                                          </p>
                                        )}
                                        {deliveryDate && (
                                          <p className="text-xs text-muted-foreground mt-1">
                                            {new Date(deliveryDate).toLocaleDateString('fr-FR', {
                                              day: '2-digit', month: 'short', year: 'numeric',
                                              hour: '2-digit', minute: '2-digit'
                                            })}
                                          </p>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </CardContent>
                    )}
                  </Card>
                );
              })
            )}
          </TabsContent>

          {/* ============ INCIDENTS TAB ============ */}
          <TabsContent value="incidents" className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold">Mes incidents signalés</h3>
              <Button size="sm" onClick={() => openIncidentDialog('')}>
                <AlertTriangle className="h-4 w-4 mr-1" /> Signaler un incident
              </Button>
            </div>

            {incidentsLoading ? (
              <div className="flex justify-center py-8">
                <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : incidents.length === 0 ? (
              <Card>
                <CardContent className="pt-6">
                  <div className="text-center py-8">
                    <AlertTriangle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">Aucun incident signalé</p>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {incidents.map((inc) => (
                  <Card key={inc.id}>
                    <CardContent className="pt-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-mono text-sm font-medium">{inc.package_oce_code || '-'}</span>
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${INCIDENT_STATUS_COLORS[inc.status] || 'bg-muted text-foreground'}`}>
                              {inc.status}
                            </span>
                          </div>
                          <p className="text-sm text-foreground font-medium">
                            {(INCIDENT_CATEGORIES[language] || INCIDENT_CATEGORIES.fr)[inc.category] || inc.category}
                          </p>
                          <p className="text-sm text-muted-foreground mt-1">{inc.description}</p>
                          {inc.resolution_note && (
                            <p className="text-sm text-green-700 mt-2 p-2 bg-green-500/10 rounded">
                              Résolution : {inc.resolution_note}
                            </p>
                          )}
                        </div>
                        <span className="text-xs text-muted-foreground ml-4">
                          {inc.created_at ? new Date(inc.created_at).toLocaleDateString('fr-FR') : ''}
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Expenses Tab */}
          {expenseFeatureEnabled && (
            <TabsContent value="expenses" className="space-y-4">
              {/* KPIs */}
              {(() => {
                const pending = expenses.filter(e => e.status === 'pending');
                const approved = expenses.filter(e => e.status === 'approved');
                const rejected = expenses.filter(e => e.status === 'rejected');
                const fmt = v => Number(v || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
                return (
                  <>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      <Card><CardContent className="pt-4 text-center">
                        <div className="text-2xl font-bold">{expenses.length}</div>
                        <div className="text-xs text-muted-foreground">{language === 'en' ? 'Total submitted' : 'Total soumises'}</div>
                      </CardContent></Card>
                      <Card className="border-yellow-200"><CardContent className="pt-4 text-center">
                        <div className="text-2xl font-bold text-yellow-600">{fmt(pending.reduce((s, e) => s + e.amount, 0))}</div>
                        <div className="text-xs text-muted-foreground">{pending.length} {language === 'en' ? 'pending' : 'en attente'}</div>
                      </CardContent></Card>
                      <Card className="border-green-200"><CardContent className="pt-4 text-center">
                        <div className="text-2xl font-bold text-green-600">{fmt(approved.reduce((s, e) => s + e.amount, 0))}</div>
                        <div className="text-xs text-muted-foreground">{approved.length} {language === 'en' ? 'approved' : 'approuvées'}</div>
                      </CardContent></Card>
                      <Card className="border-red-200"><CardContent className="pt-4 text-center">
                        <div className="text-2xl font-bold text-red-600">{fmt(rejected.reduce((s, e) => s + e.amount, 0))}</div>
                        <div className="text-xs text-muted-foreground">{rejected.length} {language === 'en' ? 'rejected' : 'rejetées'}</div>
                      </CardContent></Card>
                    </div>

                    <div className="flex justify-between items-center">
                      <h3 className="text-lg font-semibold">{language === 'en' ? 'My expense reports' : 'Mes notes de frais'}</h3>
                      <Button size="sm" onClick={() => setShowExpenseDialog(true)}>
                        <DollarSign className="h-4 w-4 mr-1" />
                        {language === 'en' ? 'New expense' : 'Nouvelle note'}
                      </Button>
                    </div>

                    {expensesLoading ? (
                      <div className="text-center text-muted-foreground py-8">{t('loading')}</div>
                    ) : expenses.length === 0 ? (
                      <Card><CardContent className="pt-6 text-center text-muted-foreground py-8">
                        {language === 'en' ? 'No expense reports yet' : 'Aucune note de frais'}
                      </CardContent></Card>
                    ) : (
                      <div className="space-y-3">
                        {expenses.map(exp => {
                          const statusColors = { pending: 'bg-yellow-100 text-yellow-800', approved: 'bg-green-100 text-green-800', rejected: 'bg-red-100 text-red-800' };
                          const statusLabels = language === 'en' ? { pending: 'Pending', approved: 'Approved', rejected: 'Rejected' } : { pending: 'En attente', approved: 'Approuvée', rejected: 'Rejetée' };
                          return (
                            <Card key={exp.id}>
                              <CardContent className="pt-4">
                                <div className="flex items-start justify-between">
                                  <div className="flex-1 space-y-1">
                                    <div className="flex items-center gap-2">
                                      <Badge variant="outline" className="text-xs">{catLabels[exp.category] || exp.category}</Badge>
                                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusColors[exp.status]}`}>
                                        {statusLabels[exp.status]}
                                      </span>
                                    </div>
                                    <div className="text-lg font-bold">{Number(exp.amount).toLocaleString('fr-FR')} €</div>
                                    <div className="text-sm text-muted-foreground">{exp.description}</div>
                                    <div className="text-xs text-muted-foreground">
                                      {language === 'en' ? 'Date' : 'Date'}: {exp.expense_date} — {language === 'en' ? 'Submitted' : 'Soumise'}: {new Date(exp.created_at).toLocaleDateString('fr-FR')}
                                    </div>
                                    {exp.status === 'rejected' && exp.review_note && (
                                      <div className="text-xs text-red-600 mt-1">
                                        {language === 'en' ? 'Reason' : 'Motif'}: {exp.review_note}
                                      </div>
                                    )}
                                    {exp.status === 'approved' && exp.reviewed_by_name && (
                                      <div className="text-xs text-green-600 mt-1">
                                        {language === 'en' ? 'Approved by' : 'Approuvée par'} {exp.reviewed_by_name}
                                      </div>
                                    )}
                                  </div>
                                  {exp.status === 'pending' && (
                                    <Button variant="ghost" size="sm" className="text-red-500" onClick={() => handleDeleteExpense(exp.id)}>
                                      <X className="h-4 w-4" />
                                    </Button>
                                  )}
                                </div>
                              </CardContent>
                            </Card>
                          );
                        })}
                      </div>
                    )}
                  </>
                );
              })()}
            </TabsContent>
          )}
        </Tabs>

        {/* Dialog Nouvelle Note de Frais */}
        {showExpenseDialog && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-card p-4 sm:p-6 rounded-lg w-full max-w-[calc(100vw-2rem)] sm:max-w-md">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <FileText className="h-5 w-5 text-blue-500" />
                {language === 'en' ? 'New expense report' : 'Nouvelle note de frais'}
              </h3>
              <form onSubmit={handleCreateExpense} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    {language === 'en' ? 'Category' : 'Catégorie'} *
                  </label>
                  <select
                    required
                    value={expenseForm.category}
                    onChange={(e) => setExpenseForm({ ...expenseForm, category: e.target.value })}
                    className="w-full p-2 border border-border rounded-md text-sm"
                  >
                    {Object.entries(catLabels).map(([k, v]) => (
                      <option key={k} value={k}>{v}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    {language === 'en' ? 'Amount (€)' : 'Montant (€)'} *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    required
                    value={expenseForm.amount}
                    onChange={(e) => setExpenseForm({ ...expenseForm, amount: e.target.value })}
                    className="w-full p-2 border border-border rounded-md text-sm"
                    placeholder="25.00"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    Description *
                  </label>
                  <textarea
                    required
                    maxLength={500}
                    value={expenseForm.description}
                    onChange={(e) => setExpenseForm({ ...expenseForm, description: e.target.value })}
                    className="w-full p-2 border border-border rounded-md text-sm"
                    rows={3}
                    placeholder={language === 'en' ? 'Describe the expense...' : 'Décrivez la dépense...'}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    {language === 'en' ? 'Expense date' : 'Date de la dépense'} *
                  </label>
                  <input
                    type="date"
                    required
                    value={expenseForm.expense_date}
                    onChange={(e) => setExpenseForm({ ...expenseForm, expense_date: e.target.value })}
                    className="w-full p-2 border border-border rounded-md text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    {language === 'en' ? 'Receipt photo (optional)' : 'Photo justificatif (optionnel)'}
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        const reader = new FileReader();
                        reader.onload = (ev) => setExpenseForm(prev => ({ ...prev, receipt_photo: ev.target.result }));
                        reader.readAsDataURL(file);
                      }
                    }}
                    className="w-full p-2 border border-border rounded-md text-sm"
                  />
                </div>
                <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setShowExpenseDialog(false)} className="w-full sm:w-auto">
                    {t('cancel')}
                  </Button>
                  <Button type="submit" disabled={expenseSubmitting} className="w-full sm:w-auto">
                    {expenseSubmitting ? t('loading') : (language === 'en' ? 'Submit' : 'Soumettre')}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Dialog Signalement Incident */}
        {showIncidentDialog && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-card p-4 sm:p-6 rounded-lg w-full max-w-[calc(100vw-2rem)] sm:max-w-md">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-orange-500" />
                Signaler un incident
              </h3>

              <form onSubmit={handleSubmitIncident} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    Code colis (ID) *
                  </label>
                  <input
                    type="text"
                    required
                    value={incidentPackageId}
                    onChange={(e) => setIncidentPackageId(e.target.value)}
                    className="w-full p-2 border border-border rounded-md text-sm"
                    placeholder="ID du colis"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    Catégorie *
                  </label>
                  <select
                    required
                    value={incidentCategory}
                    onChange={(e) => setIncidentCategory(e.target.value)}
                    className="w-full p-2 border border-border rounded-md text-sm"
                  >
                    <option value="">-- Sélectionner --</option>
                    {Object.entries(INCIDENT_CATEGORIES[language] || INCIDENT_CATEGORIES.fr).map(([key, label]) => (
                      <option key={key} value={key}>{label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    Description *
                  </label>
                  <textarea
                    required
                    value={incidentDescription}
                    onChange={(e) => setIncidentDescription(e.target.value)}
                    className="w-full p-2 border border-border rounded-md text-sm"
                    rows={3}
                    maxLength={1000}
                    placeholder="Décrivez le problème..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    Photo (optionnel)
                  </label>
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f && f.size > 5 * 1024 * 1024) {
                        setError('Photo trop volumineuse (max 5MB)');
                        return;
                      }
                      setIncidentPhoto(f || null);
                    }}
                    className="w-full text-sm"
                  />
                </div>

                <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowIncidentDialog(false)}
                    className="w-full sm:w-auto"
                  >
                    Annuler
                  </Button>
                  <Button
                    type="submit"
                    className="w-full sm:w-auto bg-orange-500 hover:bg-orange-600"
                    disabled={incidentSubmitting}
                  >
                    {incidentSubmitting ? (
                      <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
                    ) : (
                      <AlertTriangle className="h-4 w-4 mr-1" />
                    )}
                    Signaler
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Dialog Unifié Livraison */}
        {showDeliveryFlow && deliveryFlowPkg && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-card p-4 sm:p-6 rounded-lg w-full max-w-[calc(100vw-2rem)] sm:max-w-md max-h-[90vh] overflow-y-auto">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Truck className="h-5 w-5 text-green-600" />
                Livrer le colis
              </h3>

              <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded">
                <p className="font-medium text-green-800">{deliveryFlowPkg.oce_code}</p>
                <p className="text-sm text-green-700">{deliveryFlowPkg.recipient_name}</p>
                {deliveryFlowPkg.description && (
                  <p className="text-xs text-green-600 mt-1">{deliveryFlowPkg.description}</p>
                )}
              </div>

              {/* Step 1: Choose delivery mode */}
              {deliveryStep === 'choose' && (
                <div className="space-y-3">
                  <p className="text-sm font-medium text-muted-foreground">Comment souhaitez-vous livrer ce colis ?</p>
                  <button
                    type="button"
                    onClick={() => {
                      setDeliveryStep('hand');
                      setDeliveryNotes(`Votre colis ${deliveryFlowPkg.oce_code} a été livré avec succès en main propre.`);
                    }}
                    className="w-full flex items-start gap-3 p-4 border-2 border-border rounded-lg hover:border-green-400 hover:bg-green-50 transition-colors text-left"
                  >
                    <User className="h-6 w-6 text-green-600 mt-0.5 shrink-0" />
                    <div>
                      <p className="font-semibold text-sm">Livraison en main propre</p>
                      <p className="text-xs text-muted-foreground">Remettre directement au destinataire avec signature</p>
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setDeliveryStep('relay');
                      setRelayNotes(`Votre colis ${deliveryFlowPkg.oce_code} vient d'être déposé en point relais. Vous serez notifié dès qu'il sera disponible pour retrait.`);
                    }}
                    className="w-full flex items-start gap-3 p-4 border-2 border-border rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-colors text-left"
                  >
                    <MapPin className="h-6 w-6 text-blue-600 mt-0.5 shrink-0" />
                    <div>
                      <p className="font-semibold text-sm">Livraison en point relais</p>
                      <p className="text-xs text-muted-foreground">Déposer dans un point relais pour retrait</p>
                    </div>
                  </button>
                  <div className="flex justify-end pt-2">
                    <Button variant="outline" onClick={closeDeliveryFlow}>
                      Annuler
                    </Button>
                  </div>
                </div>
              )}

              {/* Step 2a: Hand delivery — code + signature */}
              {deliveryStep === 'hand' && (
                <div className="space-y-4">
                  <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 p-2 rounded">
                    <User className="h-4 w-4" />
                    <span className="font-medium">Livraison en main propre</span>
                  </div>

                  {/* Code de vérification — affiché dès que l'entreprise exige l'OTP.
                      Si le colis n'est pas encore "en livraison", le code n'a pas été
                      envoyé : bouton « Envoyer le code au client » (déclenche l'envoi). */}
                  {(otpEnabled || deliveryFlowPkg?.status === 'out_for_delivery') && (
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1 flex items-center gap-1">
                        <KeyRound className="h-4 w-4 text-amber-600" />
                        Code de livraison (demandez-le au destinataire) *
                      </label>
                      <input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        maxLength={6}
                        value={deliveryCode}
                        onChange={(e) => setDeliveryCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        className="w-full p-3 border border-border rounded-md text-center text-xl sm:text-2xl tracking-widest font-mono"
                        placeholder="______"
                      />
                      {deliveryFlowPkg?.status === 'out_for_delivery' ? (
                        <button
                          type="button"
                          onClick={handleResendCode}
                          disabled={resendCooldown > 0}
                          className="mt-1 text-xs text-blue-600 hover:text-blue-800 disabled:text-gray-400 flex items-center gap-1"
                        >
                          <Send className="h-3 w-3" />
                          {resendCooldown > 0
                            ? `Renvoyer le code (${resendCooldown}s)`
                            : 'Renvoyer le code au client'}
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => handleStartDelivery(deliveryFlowPkg)}
                          disabled={startingDelivery === deliveryFlowPkg?.id}
                          className="mt-1 text-xs text-blue-600 hover:text-blue-800 disabled:text-gray-400 flex items-center gap-1"
                        >
                          <Send className="h-3 w-3" />
                          {startingDelivery === deliveryFlowPkg?.id ? 'Envoi…' : 'Envoyer le code au client'}
                        </button>
                      )}
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">
                      Notes de livraison
                    </label>
                    <textarea
                      value={deliveryNotes}
                      onChange={(e) => setDeliveryNotes(e.target.value)}
                      className="w-full p-2 border border-border rounded-md text-sm"
                      rows={3}
                      placeholder="Informations sur la livraison..."
                    />
                  </div>

                  <SignaturePad
                    label="Signature du destinataire"
                    height={120}
                    onSignatureChange={(sig) => setDeliverySignature(sig)}
                  />

                  {/* Le bouton Livrer est verrouillé tant que le code de livraison (6
                      chiffres) n'est pas saisi, quand l'entreprise exige l'OTP. */}
                  {(otpEnabled || deliveryFlowPkg?.status === 'out_for_delivery') && deliveryCode.length !== 6 && (
                    <p className="text-xs text-amber-600 flex items-center gap-1">
                      <KeyRound className="h-3 w-3" />
                      Saisissez le code à 6 chiffres communiqué par le client pour débloquer la livraison.
                    </p>
                  )}

                  <div className="flex flex-col-reverse sm:flex-row sm:justify-between gap-2 pt-2">
                    <Button variant="outline" onClick={() => setDeliveryStep('choose')} className="w-full sm:w-auto">
                      Retour
                    </Button>
                    <Button
                      onClick={handleHandDelivery}
                      disabled={
                        deliverySubmitting ||
                        ((otpEnabled || deliveryFlowPkg?.status === 'out_for_delivery') && deliveryCode.length !== 6)
                      }
                      className="w-full sm:w-auto bg-green-600 hover:bg-green-700"
                    >
                      {deliverySubmitting ? (
                        <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
                      ) : (
                        <CheckCircle className="h-4 w-4 mr-1" />
                      )}
                      Livrer
                    </Button>
                  </div>
                </div>
              )}

              {/* Step 2b: Relay point delivery */}
              {deliveryStep === 'relay' && (
                <div className="space-y-4">
                  <div className="flex items-center gap-2 text-sm text-blue-700 bg-blue-50 p-2 rounded">
                    <MapPin className="h-4 w-4" />
                    <span className="font-medium">Livraison en point relais</span>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">
                      Ville (point relais) *
                    </label>
                    <select
                      value={relayCity}
                      onChange={(e) => setRelayCity(e.target.value)}
                      className="w-full p-2 border border-border rounded-md text-sm"
                    >
                      <option value="">-- Choisir une ville --</option>
                      {relayCities.map(city => (
                        <option key={city.code || city.name} value={city.name}>
                          {city.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {relayCity && (
                    <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm">
                      <p className="font-semibold text-blue-800 flex items-center gap-1">
                        <MapPin className="h-4 w-4" /> Point relais — {relayCity}
                      </p>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">
                      Photo du bon d'expédition *
                    </label>
                    <input
                      type="file"
                      accept="image/*"
                      capture="environment"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        const reader = new FileReader();
                        reader.onload = (ev) => {
                          setRelayPhoto({ file, preview: ev.target.result, base64: ev.target.result });
                        };
                        reader.readAsDataURL(file);
                        e.target.value = '';
                      }}
                      className="w-full p-2 border border-border rounded-md text-sm"
                    />
                    {relayPhoto?.preview ? (
                      <div className="relative mt-2">
                        <img src={relayPhoto.preview} alt="Bon" className="w-full h-32 sm:h-40 object-cover rounded border" />
                        <button
                          type="button"
                          onClick={() => setRelayPhoto(null)}
                          className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs"
                        >
                          ✕
                        </button>
                      </div>
                    ) : (
                      <p className="text-xs text-muted-foreground mt-1">
                        Prenez en photo le bon d'expédition du point relais
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">
                      Notes
                    </label>
                    <textarea
                      value={relayNotes}
                      onChange={(e) => setRelayNotes(e.target.value)}
                      className="w-full p-2 border border-border rounded-md text-sm"
                      rows={3}
                      placeholder="Notes de dépôt..."
                    />
                  </div>

                  <div className="flex flex-col-reverse sm:flex-row sm:justify-between gap-2 pt-2">
                    <Button variant="outline" onClick={() => setDeliveryStep('choose')} className="w-full sm:w-auto">
                      Retour
                    </Button>
                    <Button
                      onClick={handleRelayDelivery}
                      disabled={deliverySubmitting || !relayCity || !relayPhoto}
                      className="w-full sm:w-auto bg-green-600 hover:bg-green-700"
                    >
                      {deliverySubmitting ? (
                        <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
                      ) : (
                        <CheckCircle className="h-4 w-4 mr-1" />
                      )}
                      Livrer
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Dialog Enregistrement Paiement */}
        {showPaymentDialog && paymentPackage && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-card p-4 sm:p-6 rounded-lg w-full max-w-[calc(100vw-2rem)] sm:max-w-md">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Banknote className="h-5 w-5 text-emerald-600" />
                Enregistrer un paiement
              </h3>

              <div className="mb-4 p-3 bg-emerald-50 border border-emerald-200 rounded">
                <p className="font-medium text-emerald-800">{paymentPackage.oce_code}</p>
                <p className="text-sm text-emerald-700">{paymentPackage.recipient_name}</p>
                <div className="mt-2 text-sm">
                  <span className="text-muted-foreground">Montant total : </span>
                  <span className="font-semibold">{fmt(paymentPackage.price || paymentPackage.total_price || 0)}</span>
                </div>
                {(() => {
                  const price = paymentPackage.price || paymentPackage.total_price || 0;
                  const currentPaid = paymentPackage.payment_status === 'paid' ? price : (paymentPackage.advance_amount || 0);
                  const remaining = Math.max(0, price - currentPaid);
                  return (
                    <>
                      {currentPaid > 0 && (
                        <div className="text-sm">
                          <span className="text-muted-foreground">Déjà payé : </span>
                          <span className="font-medium text-green-700">{fmt(currentPaid)}</span>
                        </div>
                      )}
                      {remaining > 0 && (
                        <div className="text-sm">
                          <span className="text-muted-foreground">Reste à collecter : </span>
                          <span className="font-medium text-red-700">{fmt(remaining)}</span>
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>

              <form onSubmit={handleRecordPayment} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    Montant reçu *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    required
                    value={paymentAmount}
                    onChange={(e) => setPaymentAmount(e.target.value)}
                    className="w-full p-2 border border-border rounded-md text-sm"
                    placeholder="0.00"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    Méthode de paiement
                  </label>
                  <select
                    value={paymentMethod}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                    className="w-full p-2 border border-border rounded-md text-sm"
                  >
                    <option value="cash">Espèces</option>
                    <option value="mobile_money">Mobile Money</option>
                    <option value="bank_transfer">Virement bancaire</option>
                    <option value="card">Carte bancaire</option>
                    <option value="other">Autre</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    Note (optionnel)
                  </label>
                  <textarea
                    value={paymentNote}
                    onChange={(e) => setPaymentNote(e.target.value)}
                    className="w-full p-2 border border-border rounded-md text-sm"
                    rows={2}
                    maxLength={500}
                    placeholder="Détails sur le paiement..."
                  />
                </div>

                <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setShowPaymentDialog(false);
                      setPaymentPackage(null);
                    }}
                    className="w-full sm:w-auto"
                  >
                    Annuler
                  </Button>
                  <Button
                    type="submit"
                    className="w-full sm:w-auto bg-emerald-600 hover:bg-emerald-700"
                    disabled={paymentSubmitting || !paymentAmount}
                  >
                    {paymentSubmitting ? (
                      <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
                    ) : (
                      <Banknote className="h-4 w-4 mr-1" />
                    )}
                    Enregistrer le paiement
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>

      {/* Déchargement : scan réception. Le bouton « Signaler manquants » du dialog
          marque les colis embarqués non reçus comme manquants (incidents). */}
      {unloadScan.shipmentId && (
        <ShipmentScanDialog
          open={unloadScan.open}
          onOpenChange={(open) => {
            setUnloadScan((prev) => ({ ...prev, open }));
            if (!open) {
              fetchShipments();
              if (selectedShipment) fetchShipmentPackages(selectedShipment);
            }
          }}
          shipmentId={unloadScan.shipmentId}
          checkpoint="reception"
          onScanned={() => { if (selectedShipment) fetchShipmentPackages(selectedShipment); }}
        />
      )}
    </ProfessionalLayout>
  );
};

export default LocalPartnerDashboard;